import { z } from 'zod';
export const REMINDER_MAX_STEPS = 5;
/**
 * Default cap on the schedules an organisation can have: the default, any others, and
 * client schedules all count. Overridable per tenant from the console app.
 */
export const REMINDER_MAX_SCHEDULES = 5;
/** Highest cap the console app accepts for a tenant. */
export const REMINDER_MAX_SCHEDULES_CEILING = 50;
export const REMINDER_MAX_DAYS_BEFORE_DUE = 7;
export const REMINDER_MAX_DAYS_AFTER_DUE = 30;
export const ReminderStepSchema = z
    .object({
    daysRelativeToDue: z
        .number()
        .int()
        .min(-REMINDER_MAX_DAYS_BEFORE_DUE, {
        message: `Reminders can start at most ${REMINDER_MAX_DAYS_BEFORE_DUE} days before the due date.`,
    })
        .max(REMINDER_MAX_DAYS_AFTER_DUE, {
        message: `Reminders can run at most ${REMINDER_MAX_DAYS_AFTER_DUE} days after the due date.`,
    }),
    emailClient: z.boolean(),
    alertStaff: z.boolean(),
})
    .refine((step) => step.emailClient || step.alertStaff, {
    message: 'Choose at least one: email the client or alert you.',
    path: ['emailClient'],
});
export const ReminderRepeatSchema = z.object({
    every: z.number().int().min(1).max(REMINDER_MAX_DAYS_AFTER_DUE),
    max: z.number().int().min(1).max(REMINDER_MAX_DAYS_AFTER_DUE),
});
function lastEmailDay(steps) {
    const emailDays = steps
        .filter((step) => step.emailClient)
        .map((step) => step.daysRelativeToDue);
    return emailDays.length > 0 ? Math.max(...emailDays) : null;
}
export const ReminderStepsSchema = z
    .array(ReminderStepSchema)
    .min(1, { message: 'Add at least one reminder.' })
    .max(REMINDER_MAX_STEPS, {
    message: `A schedule can have at most ${REMINDER_MAX_STEPS} reminders.`,
})
    .superRefine((steps, context) => {
    // Flag every repeat of a day on its own row so the form can show it under that step.
    const seenDays = new Set();
    steps.forEach((step, index) => {
        if (seenDays.has(step.daysRelativeToDue)) {
            context.addIssue({
                code: 'custom',
                path: [index, 'daysRelativeToDue'],
                message: 'Another step already uses this day.',
            });
        }
        seenDays.add(step.daysRelativeToDue);
    });
});
/**
 * Cross-field check that the repeat rule has an email step to follow and finishes by +30.
 * Skips when either side is absent so partial updates can reuse it.
 */
export function refineReminderRepeat(schedule, context) {
    if (!schedule.repeat || !schedule.steps)
        return;
    const lastDay = lastEmailDay(schedule.steps);
    if (lastDay === null) {
        context.addIssue({
            code: 'custom',
            path: ['repeat'],
            message: 'Repeating needs at least one reminder that emails the client.',
        });
        return;
    }
    const finalDay = lastDay + schedule.repeat.every * schedule.repeat.max;
    if (finalDay > REMINDER_MAX_DAYS_AFTER_DUE) {
        context.addIssue({
            code: 'custom',
            path: ['repeat'],
            message: `Repeats must finish within ${REMINDER_MAX_DAYS_AFTER_DUE} days after the due date. This one ends on day ${finalDay}. Reduce the interval or the number of repeats.`,
        });
    }
}
export const ReminderScheduleSchema = z
    .object({
    steps: ReminderStepsSchema,
    repeat: ReminderRepeatSchema.nullable().optional(),
})
    .superRefine(refineReminderRepeat);
/** Request body for dismissing a scheduled reminder or restoring a dismissed one. */
export const UpdateInvoiceReminderSchema = z.object({
    status: z.enum(['DISMISSED', 'SCHEDULED']),
});
/** The three standard reminder emails: before the due date, on it, and after it. */
export const ReminderEmailStageSchema = z.enum(['BEFORE_DUE', 'DUE_DATE', 'OVERDUE']);
function pluralDays(count) {
    return count === 1 ? '1 day' : `${count} days`;
}
/** Day offset in words: "3 days before the due date", "on the due date". */
export function describeReminderOffset(daysRelativeToDue) {
    if (daysRelativeToDue === 0)
        return 'on the due date';
    return daysRelativeToDue < 0
        ? `${pluralDays(-daysRelativeToDue)} before the due date`
        : `${pluralDays(daysRelativeToDue)} after the due date`;
}
function describeOffsets(days) {
    const clauses = days.map(describeReminderOffset);
    if (clauses.length === 1)
        return clauses[0];
    return `${clauses.slice(0, -1).join(', ')} and ${clauses[clauses.length - 1]}`;
}
/** Short timeline label for a day offset: "-3d", "Due" or "+7d". */
export function describeReminderMarkLabel(daysRelativeToDue) {
    if (daysRelativeToDue === 0)
        return 'Due';
    return daysRelativeToDue < 0 ? `${daysRelativeToDue}d` : `+${daysRelativeToDue}d`;
}
/**
 * Plain-English sentence and timeline marks for a reminder schedule, shared by Settings,
 * the invoice form and the client page. Steps are sorted by day; repeat marks follow the
 * last step that emails the client.
 */
export function describeReminderSchedule(steps, repeat) {
    const sortedSteps = [...steps].sort((a, b) => a.daysRelativeToDue - b.daysRelativeToDue);
    const emailDays = sortedSteps
        .filter((step) => step.emailClient)
        .map((step) => step.daysRelativeToDue);
    const alertDays = sortedSteps
        .filter((step) => step.alertStaff)
        .map((step) => step.daysRelativeToDue);
    const sentences = [
        emailDays.length > 0
            ? `After an invoice is emailed, myTrellis emails the client ${describeOffsets(emailDays)}.`
            : 'myTrellis does not email the client: no step sends an email.',
    ];
    if (repeat && emailDays.length > 0) {
        sentences.push(`After that it keeps emailing every ${pluralDays(repeat.every)}, up to ${repeat.max} more ${repeat.max === 1 ? 'time' : 'times'}.`);
    }
    if (alertDays.length > 0)
        sentences.push(`You get an alert ${describeOffsets(alertDays)}.`);
    sentences.push('Reminders stop when the invoice is paid or voided.');
    const marks = sortedSteps.map((step) => ({
        daysRelativeToDue: step.daysRelativeToDue,
        label: describeReminderMarkLabel(step.daysRelativeToDue),
        emailClient: step.emailClient,
        alertStaff: step.alertStaff,
        isRepeat: false,
    }));
    const finalEmailDay = emailDays.length > 0 ? Math.max(...emailDays) : null;
    if (repeat && finalEmailDay !== null) {
        for (let index = 1; index <= repeat.max; index++) {
            const day = finalEmailDay + repeat.every * index;
            marks.push({
                daysRelativeToDue: day,
                label: describeReminderMarkLabel(day),
                emailClient: true,
                alertStaff: false,
                isRepeat: true,
            });
        }
    }
    return { summary: sentences.join(' '), marks };
}
//# sourceMappingURL=reminders.js.map